import { Injectable } from '@angular/core';
import { Product, Price, Banner } from '@models/product';
import { Order, Transaction} from  '@models/order';
import { StockService } from '@services/stock.service';
import { NotifyService } from '@services/notify.service';
import { ProductService } from './product.service';
import { ProductFilter } from '../models/filter';

@Injectable({
	providedIn: 'root'
})
export class CartService{
	private lines: LineCart[]=[];
  private giftProduct: Product | null = null;

	constructor(
    private notify: NotifyService,
    private sService: StockService,
    private productServ: ProductService
  ) {
		this.load();
    this.loadGiftProduct();
	}

  /**
   * Load the gift product.
   * The gift product is a product with name 'Emballage cadeau'.
   * If the product is found, it is stored in the service.
   * If an error occurs, it is logged to the console.
   */
  private loadGiftProduct() {
    let filter: ProductFilter=new ProductFilter();
    filter.name = 'Emballage cadeau';
    this.productServ.searchProducts(filter, 0, 1)
      .subscribe({
        next: (products: string | any[]) => {
          if (products.length > 0) {
            this.giftProduct = products[0];
          }
        },
        error: (err: any) => console.error('Failed to load gift product:', err)
      });
  }

	add(product: Product, price: Price, qte: number, callback?: (success: boolean) => void){
		this.sService.getStocks(product.ident).subscribe(stocks => {
			product.stocks=stocks;
			let success: boolean=this.addCart(product, price, qte);
			if(callback)
				callback(success);
		});
	}

	addCart(product: Product, price: Price, qte: number){
		let success: boolean=false;
		if(!this.canOrder(product))
			this.notify.set('Panier', 'Stocks insuffisants');
		else if(qte>0){
			let qteCart: number=this.lines.reduce((acc, current) => {
				if(current.product.ident==product.ident)
					return acc+=current.qte*current.price.qte;
				else
					return acc;
			}, 0);
			let line: LineCart=this.lines.find(l => l.price.ident==price.ident);

			if(!product.limitOrder || qteCart+qte*price.qte<=product.limitOrder){
				if(line)
					line.qte+=qte;
				else
					this.lines.push(new LineCart(product, price, qte));
				this.notify.set('Panier', 'Produit(s) ajouté(s) au panier');
				success=true;
			}
			else
				this.notify.set('Panier', `Quantité limitée à ${product.limitOrder}`);
			this.save();
		}
		else
			this.notify.set('Panier', 'Ajout au panier impossible');
		return success;
	}

	/**
	 * Checks if a product can be ordered or not.
	 * @param product the product to check
	 * @returns true if the product can be ordered, false otherwise
	 */
	canOrder(product: Product){
    // A product with no stock must always be in rupture
    return (product.state!='rupture');
	}

/**
 * Determines if a product is available for online ordering only.
 * @param product The product to check.
 * @returns true if the product is not in rupture state and has no order limit, indicating that it is available for online ordering only; false otherwise.
 */
  orderOnlineOnly(product: Product){
		let limitOrder: number=product.limitOrder;
    // A product with no stock must always be in rupture
    return (product.state!='rupture' && limitOrder == 0);
	}

  /**
   * Returns the release date of a product if it is a pre-order, null otherwise.
   * @param product The product to check.
   * @returns The release date of the product if it is a pre-order, null otherwise.
   */
  getPreOrderDateIfAny(product: Product) {
    if (!product.banners || product.banners.length === 0) return null;

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const todayTime = todayMidnight.getTime();

    const  releaseDate: Date=new Date(product.releaseDate);

    const  isPreordered: boolean = !!product.banners.find(banner => {
      if (!banner.start || !banner.end || !banner.name) return false; // ensure values exist

      const startTime = new Date(banner.start).setHours(0, 0, 0, 0);
      const endTime = new Date(banner.end).setHours(0, 0, 0, 0);
      return (
        banner.name === 'Précommande' &&
        startTime <= todayTime &&
        endTime >= todayTime
      );
    }

    );
    /* console.log('today:', todayTime, 'isperordered', isPreordered, 'banners', product.banners);
    console.log(new Date(product.banners[0].start)); */
    return (isPreordered) ? releaseDate : null;
  }

	rm(i: number){
		this.lines.splice(i, 1);
		this.save();
	}

	count(){
		return this.lines.length;
	}

	getLines(){
		return this.lines;
	}

	getAmount(){
		let amount: number=this.getLines().reduce((acc, current) => acc+=current.qte*current.price.prix, 0);
		return amount;
	}

	save(){
		localStorage.setItem('cart', JSON.stringify(this.lines));
	}

	load(){
		this.lines=JSON.parse(localStorage.getItem('cart'));
		if(!this.lines){
			this.lines=[];
			this.save();
		}
	}

	clear(){
		this.lines=[];
		this.save();
		localStorage.setItem('order', null);
	}

	maxQte(product: Product, price: Price){
		let limitOrder: number=null;
    let productStock: number=(product.stocks ?? []).reduce((acc: any, current: { qte: any; }) => acc + current.qte, 0);

    if (product.limitOrder > 0) {
      limitOrder = Math.floor(product.limitOrder / price.qte);
      if (limitOrder < productStock)
        return limitOrder;
    }

    return Math.max(0, productStock);
	}

	setOrder(order: Order=null){
    let giftWrappingQty: number=this.getLines().reduce((acc, current) => acc+=current.giftWrapping, 0);

		if(!order){
			order=new Order();
			this.getLines().forEach(line => order.transactions.push(new Transaction(line.price.ident, line.qte, (line.giftWrapping >= 1) ? 'true' : 'false' )));
      // Handles gift wrapping
      if (giftWrappingQty > 0) {
        order.transactions.push(new Transaction(this.giftProduct.prices[0].ident, giftWrappingQty, 'false'));
      }
		}
		localStorage.setItem('order', JSON.stringify(order));
		return order;
	}

	getOrder(){
		if(localStorage.getItem('order'))
			return JSON.parse(localStorage.getItem('order'));
		else
			return this.setOrder();
	}
}

export class LineCart{
	constructor(
		public product: Product,
		public price: Price,
		public qte: number,
    public giftWrapping: number = 0,
    public maxReached?: boolean
	){ }
}
